import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from 'hooks/AuthContext'
import { useToast } from 'provider/ToastProvider'

/**
 * RequirePaymentGate component - Protects routes that require a valid subscription without pending payments.
 * 
 * This component checks if the user is logged in and if their subscription requires payment.
 * If payment is required, it redirects the user to the payment page.
 * If the user is not logged in, it allows access to the route (public access).
 * 
 * @component RequirePaymentGate
 * @returns {JSX.Element} RequirePaymentGate component
 */
const RequirePaymentGate: React.FC = () => {
    const { loading, user, userSubscription } = useAuth();
    const location = useLocation();
    const { addToast } = useToast();

    // 1. If user is not logged in, allow access to the route (allow public access, no loading state)
    if (!loading && !user) {
        return <Outlet />;
    }

    if (loading && user) {
        addToast({
            type: 'info',
            message: 'Checking subscription status...'
        })
        return <div>Loading subscription status...</div>;
    }

    const needsPayment = !!userSubscription?.paymentRequired;
    if (needsPayment && location.pathname !== '/subscription/payment') {
        return <Navigate to="/subscription/payment" state={{ from: location }} replace />;
    }

    return <Outlet />;
}

export default RequirePaymentGate;